// HOW FAR AWAY FULL DETAIL IS WORTH PAYING FOR.
//
// Owner, 2026-10-07, on the SpaceX BA03 viewer: "the slowness, is due to
// polycount and draw calls ... decoding at lower resolution".
//
// THE BANDS ARE COMPUTED HERE, NOT TAKEN FROM THE EXPORT. The manifest
// carries maxCameraDistance per level (BA03 says 50 / 200 / 30000 ft), but a
// deployed viewer must be able to change where the model steps down without
// anybody re-exporting 800 MB - so these override the manifest's numbers and
// LODSelector is given them directly.
//
// WHAT WAS WRONG WITH THE OLD RULE, measured on BA03 (diagonal 3,364 ft,
// 1,324,415 instances):
//
//   fine   < 841 ft    (a quarter of the diagonal)   l0 - 351 MB of packs
//   medium < 26,913 ft (EIGHT diagonals)             l1 - 117 MB
//   coarse   beyond                                  l2 -  83 MB
//
// TWO FAULTS IN THAT, AND THE SECOND ONE IS WORSE.
//
// ONE - A FRACTION OF THE DIAGONAL IS THE WRONG SHAPE FOR THE FINE BAND.
// The old comment already worked out that two diagonals was too generous for
// a 175 ft building; the same reasoning applied to a 3,364 ft site gives 841
// ft, which is still further than anyone stands from anything they are
// inspecting. Full detail is about how far a human can RESOLVE detail, and
// that distance does not grow when the site does. A quarter of a small
// building is a room; a quarter of BA03 is most of the building. So the fine
// band is now capped by an absolute distance as well as a fraction - the
// fraction protects small models, the cap protects large ones.
//
// TWO - COARSE WAS UNREACHABLE, so 83 MB of geometry was exported and never
// drawn. Medium ran to eight diagonals; you cannot get eight diagonals away
// from a model you are looking at. The whole-model view - one to two
// diagonals out - therefore always landed on medium, and the cheapest tier
// existed on paper only. Medium now ends at two diagonals, which is where
// "the whole model on screen" actually sits, so pulling back past it steps
// down to coarse instead of pretending there is nothing cheaper left.
//
// WHAT THIS DOES NOT CHANGE: close-up quality. Inside the fine cap the
// geometry is exactly what it was. Only the distance at which the model as a
// whole steps down has moved.
//
// AND MEASURED ON BA03 THE SAME NIGHT, IT CHANGED NOTHING AT ALL. Read this
// before using these bands to explain a frame rate. Tom measured both
// settings on the real model, two cameras, 90 s load and 30 s settle each:
//
//   before (fine <841ft)   101-102M triangles a frame
//   after  (fine <150ft)   101-102M triangles a frame
//
// Because 99.5 per cent of the triangles were ALREADY on the coarse tier -
// trisByLod [0, 458k, 99.2M]. The per-hash pixel rule picks coarse first and
// the distance band is only a CEILING on how fine a tile may get; nothing was
// touching the ceiling, so moving it did nothing. The reasoning that produced
// the numbers above was arithmetic about the band, not a measurement of what
// the selector chose, and the two are not the same thing.
//
// The bands are still better shaped than they were - a fine band that grows
// with the site is wrong whether or not anything is currently hitting it, and
// an unreachable coarse tier is a lie in the manifest. But they are NOT a
// performance lever on this model, and the real cause of the triangle count
// is the exporter's coarse level barely reducing small repeated hardware
// (strut 908/892/892 triangles across fine/medium/coarse, lock washer
// 184/104/104, hex nut 40/40/40, over 880k instances). That is where the
// 100M comes from and that is where it has to be fixed.
//
// The ?lod= override below is what made this measurable, and it is the thing
// in this file that earned its place: it let a before-and-after run on
// production in forty minutes instead of two releases.
//
// AND IT IS COMPARABLE WITHOUT A DEPLOY. ?lod=fine,medium in feet, or
// ?lod=off for the manifest's own numbers, remembered per browser like the
// packing flags - so a before-and-after is two reloads, not two releases.

// The fine cap, in feet. Chosen as room-to-corridor scale: the distance at
// which an extra edge on a pipe fitting is still a pixel rather than a
// rounding error. It is deliberately NOT derived from the model - that
// derivation is the fault above.
const FINE_CAP_FT = 150;

// Where "the whole model is on screen" sits, in diagonals. One diagonal
// frames it tightly, two comfortably; past that is the pulled-back view
// that should be cheapest of all.
const MEDIUM_DIAGONALS = 2;

const STORE_KEY = 'bidlight.lod';
const OFF = new Set(['off', 'none', 'manifest', '0', 'false']);

function readStore() {
  try { return localStorage.getItem(STORE_KEY); } catch (e) { return null; }
}
function writeStore(v) {
  try { if (v === null) localStorage.removeItem(STORE_KEY); else localStorage.setItem(STORE_KEY, v); } catch (e) { /* blocked */ }
}

/**
 * The browser's own choice, if it has made one. '?lod=120,5000' sets the two
 * finite bands in feet; '?lod=off' hands LOD selection back to the
 * manifest; '?lod=default' forgets the choice.
 *
 * Returns { bands } with bands = null for "use the manifest", or undefined
 * when no choice has been made at all. The two are different answers and
 * collapsing them is how an override becomes a silent disable.
 */
export function lodOverride(search, store = { read: readStore, write: writeStore }) {
  // THE GUARD BELONGS AT THE CALL, NOT IN ONE IMPLEMENTATION OF THE STORE.
  // readStore/writeStore catch their own throws, so the default path was
  // safe - but any OTHER store handed in here was not, and a storage read
  // that throws on load is a viewer that does not open. Caught where it is
  // called, so every store is covered by the same rule.
  const read = () => { try { return store.read(); } catch (e) { return null; } };
  const write = (v) => { try { store.write(v); } catch (e) { /* blocked */ } };
  let spec = null;
  try {
    const q = new URLSearchParams(search || (typeof window !== 'undefined' ? window.location.search : ''));
    if (q.has('lod')) spec = String(q.get('lod') || '');
  } catch (e) { spec = null; }
  if (spec !== null) {
    const low = spec.toLowerCase();
    if (low === 'default' || low === '') { write(null); return undefined; }
    write(spec);
  } else {
    spec = read();
    if (spec === null || spec === undefined) return undefined;
  }
  const low = String(spec).toLowerCase();
  if (OFF.has(low)) return { bands: null };
  const parts = String(spec).split(/[,:\s]+/).filter(Boolean).map(Number);
  const finite = parts.filter((n) => isFinite(n) && n > 0);
  // A parameter nobody can read is not a reason to change anything.
  if (finite.length < 2) return undefined;
  return { bands: [finite[0], finite[1], Infinity] };
}

/**
 * computeLodBands(manifest) -> [fine, medium, Infinity] in feet, or null to
 * use the manifest's own maxCameraDistance values.
 */
export function computeLodBands(manifest, { search, log = true, store } = {}) {
  const chosen = lodOverride(search, store || { read: readStore, write: writeStore });
  if (chosen !== undefined) {
    if (log) {
      console.log(chosen.bands
        ? `[LOD] this browser: fine <${chosen.bands[0]}ft, medium <${chosen.bands[1]}ft, coarse beyond`
        : '[LOD] this browser: using the export\'s own distances');
    }
    return chosen.bands;
  }

  const b = manifest?.scene?.trimmedBounds || manifest?.scene?.fullBounds;
  if (!b || !Array.isArray(b.min) || !Array.isArray(b.max)) return null; // fall back to manifest lods[]

  const diagonal = Math.hypot(
    b.max[0] - b.min[0],
    b.max[1] - b.min[1],
    b.max[2] - b.min[2]
  );
  if (!isFinite(diagonal) || diagonal <= 0) return null;

  const instances = manifest?.scene?.instanceCount || 0;

  // The fraction still does the work on small models, where 150 ft would be
  // the whole site and capping at it would mean nothing ever leaves fine.
  const fineMultiplier =
    instances > 200000 ? 0.25 :
    instances > 60000  ? 0.35 :
                         0.5;

  const fine = Math.min(diagonal * fineMultiplier, FINE_CAP_FT);
  const medium = Math.max(fine * 2, diagonal * MEDIUM_DIAGONALS);
  const bands = [fine, medium, Infinity];
  if (log) {
    const capped = diagonal * fineMultiplier > FINE_CAP_FT;
    console.log(
      `[LOD] scene diagonal ${diagonal.toFixed(0)}ft, ${instances} instances -> `
      + `fine <${bands[0].toFixed(0)}ft${capped ? ' (capped)' : ''}, `
      + `medium <${bands[1].toFixed(0)}ft, coarse beyond`
    );
  }
  return bands;
}

export const __test = { FINE_CAP_FT, MEDIUM_DIAGONALS };
